"use client";

import { useState } from "react";
import type { MailSender } from "@/application/mail";
import { useI18n } from "@/i18n/I18nProvider";
import { api, useApi } from "@/lib/useApi";
import { Modal, useDialog } from "@/components/Dialog";
import { Select } from "../Select";
import { Button, ErrorBox, Field, inputClass, Notice } from "../ui";
import type { TableRow } from "./SettingsTable";
import { MailGuide } from "./MailGuide";
import { useRequest } from "./useRequest";

/**
 * The platform table's row of the mail sender (administrators): the address the mails come from and its server, or
 * that none is set up, as a dot and its text — and "set up" / "change", which opens its own window. Saved by itself.
 */
export function useMailRow(): TableRow {
  const { t } = useI18n();
  const { data, reload } = useApi<{ sender: MailSender }>("/api/platform/mail");
  const [setup, setSetup] = useState(false);
  const base = { key: "mail", label: t("set.mail.row"), description: t("set.mail.hint") };
  if (!data) return { ...base, input: <div className="skeleton h-6 w-full" /> };
  const { sender } = data;
  const text = sender.sender ? t("set.mail.sending", { sender: sender.sender, server: sender.server ?? "" }) : t("set.mail.none");
  const close = () => {
    setSetup(false);
    reload();
  };
  const input = (
    <span className="flex min-w-0 items-center gap-2">
      <span aria-hidden className={`h-2 w-2 shrink-0 rounded-full ${sender.sender ? "bg-good" : "bg-line-strong"}`} />
      <span className="min-w-0 truncate" title={text}>{text}</span>
      {setup && <MailSetup sender={sender} onClose={close} />}
    </span>
  );
  const action = <Button type="button" size="xs" onClick={() => setSetup(true)}>{t(sender.sender ? "set.mail.change" : "set.mail.setUp")}</Button>;
  return { ...base, input, action };
}

const LINK = "shrink-0 cursor-pointer text-[11.5px] font-medium hover:underline disabled:opacity-50";
type Security = "ssl" | "starttls";
/** The usual port of each encryption. */
const PORT: Record<Security, number> = { ssl: 465, starttls: 587 };

/**
 * The window: the server (host, encryption, port), the account (user, password) and the sender (address, name) —
 * tested before it is kept; once kept a test mail to the administrator, and removing the sender at its foot. The ⓘ at
 * its heading explains the free way step by step (`MailGuide`).
 */
function MailSetup({ sender, onClose }: { sender: MailSender; onClose: () => void }) {
  const { t } = useI18n();
  const dialog = useDialog();
  const [form, setForm] = useState({ host: "", security: "ssl" as Security, port: PORT.ssl, user: "", password: "", sender: "", senderName: "Illustrading" });
  const [current, setCurrent] = useState(sender);
  const [tested, setTested] = useState(false);
  const { busy, error, run } = useRequest();
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));
  const save = (e: React.FormEvent) => {
    e.preventDefault();
    void run(async () => setCurrent((await api<{ sender: MailSender }>("/api/platform/mail", { method: "PUT", json: form })).sender));
  };
  const test = () => void run(async () => {
    await api("/api/platform/mail/test", { method: "POST" });
    setTested(true);
  });
  const remove = async () => {
    if (!(await dialog.confirm(t("set.mail.removeConfirm"), { danger: true }))) return;
    await run(async () => {
      await api("/api/platform/mail", { method: "DELETE" });
      onClose();
    });
  };
  const securities = (["ssl", "starttls"] as const).map((s) => ({ value: s, label: t(`set.mail.security.${s}`) }));
  return (
    <Modal label={t("set.mail.title")} onClose={onClose} size="wide">
      <form onSubmit={save} className="space-y-3 text-[13px]">
        <div className="flex items-center gap-1">
          <h2 className="text-[15px] font-semibold">{t("set.mail.title")}</h2>
          <MailGuide domain={window.location.hostname} />
        </div>
        {current.sender && <p className="text-good-ink">✓ {t("set.mail.sending", { sender: current.sender, server: current.server ?? "" })}</p>}
        <p className="text-ink-2">{t("set.mail.intro")}</p>
        <div className="grid grid-cols-[1fr_auto_6rem] gap-2 phone:grid-cols-1">
          <Field label={t("set.mail.host")}>
            <input className={inputClass} required autoComplete="off" spellCheck={false} placeholder="mx2f00.netcup.net" value={form.host} onChange={(e) => set({ host: e.target.value })} />
          </Field>
          <Field label={t("set.mail.securityLabel")}>
            <Select<Security> label={t("set.mail.securityLabel")} value={form.security} options={securities} onChange={(s) => set({ security: s, port: PORT[s] })} className="w-36 phone:w-full" />
          </Field>
          <Field label={t("set.mail.port")}>
            <input className={inputClass} required type="number" min={1} max={65535} value={form.port} onChange={(e) => set({ port: Number(e.target.value) })} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-2 phone:grid-cols-1">
          <Field label={t("set.mail.user")}>
            <input className={inputClass} required autoComplete="off" spellCheck={false} value={form.user} onChange={(e) => set({ user: e.target.value })} />
          </Field>
          <Field label={t("set.mail.password")}>
            <input className={inputClass} required type="password" autoComplete="new-password" value={form.password} onChange={(e) => set({ password: e.target.value })} />
          </Field>
          <Field label={t("set.mail.sender")} hint={t("set.mail.senderHint")}>
            <input className={inputClass} required type="email" autoComplete="off" placeholder="no-reply@illustrading.com" value={form.sender} onChange={(e) => set({ sender: e.target.value })} />
          </Field>
          <Field label={t("set.mail.senderName")}>
            <input className={inputClass} required maxLength={80} value={form.senderName} onChange={(e) => set({ senderName: e.target.value })} />
          </Field>
        </div>
        <p className="text-[11.5px] text-muted">{t("set.mail.saveHint")}</p>
        {error && <ErrorBox>{error}</ErrorBox>}
        {tested && <Notice tone="good">✓ {t("set.mail.testSent")}</Notice>}
        {/* on phones one under the other, saving on top */}
        <div className="flex items-center gap-2 border-t border-line pt-3 phone:flex-col-reverse phone:items-stretch">
          {current.sender && <button type="button" disabled={busy} onClick={() => void remove()} className={`${LINK} text-muted hover:text-bad-ink`}>{t("set.mail.remove")}</button>}
          {current.sender && <Button type="button" size="sm" disabled={busy} onClick={test}>{t("set.mail.test")}</Button>}
          <Button type="button" size="sm" onClick={onClose} className="ml-auto phone:ml-0">{t("action.close")}</Button>
          <Button type="submit" size="sm" variant="primary" disabled={busy}>{busy ? t("common.working") : t("set.mail.save")}</Button>
        </div>
      </form>
    </Modal>
  );
}
